/**
 * Custom errors for indexer operations
 */

export class IndexerError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly statusCode: number = 500,
  ) {
    super(message)
    this.name = 'IndexerError'
  }
}

export class InvalidChainIdError extends IndexerError {
  constructor(
    public readonly chainId: number | string,
    public readonly validChainIds: number[],
  ) {
    super(
      `Invalid chain ID: ${chainId}. Valid chain IDs: ${validChainIds.join(', ')}`,
      'INVALID_CHAIN_ID',
      400,
    )
    this.name = 'InvalidChainIdError'
  }
}

export class InvalidAddressError extends IndexerError {
  constructor(public readonly address: string) {
    super(`Invalid address format: ${address}. Expected 0x followed by 40 hex characters.`, 'INVALID_ADDRESS', 400)
    this.name = 'InvalidAddressError'
  }
}

export class InvalidPathError extends IndexerError {
  constructor(
    public readonly path: string,
    reason?: string,
  ) {
    super(`Invalid indexer path: ${path}${reason ? `. ${reason}` : ''}`, 'INVALID_PATH', 400)
    this.name = 'InvalidPathError'
  }
}

export class RateLimitError extends IndexerError {
  constructor(
    public readonly retryAfter: number = 60,
    public readonly requestsRemaining: number = 0,
  ) {
    super(`Rate limit exceeded. Retry after ${retryAfter} seconds.`, 'RATE_LIMIT_EXCEEDED', 429)
    this.name = 'RateLimitError'
  }
}

export class CircuitBreakerOpenError extends IndexerError {
  constructor(
    public readonly resetTime: number,
    public readonly failureCount: number,
  ) {
    super(
      `Circuit breaker is open due to ${failureCount} failures. Resets in ${Math.ceil((resetTime - Date.now()) / 1000)} seconds.`,
      'CIRCUIT_BREAKER_OPEN',
      503,
    )
    this.name = 'CircuitBreakerOpenError'
  }
}

export class IndexerTimeoutError extends IndexerError {
  constructor(
    public readonly timeoutMs: number,
    public readonly endpoint: string,
  ) {
    super(`Indexer request to ${endpoint} timed out after ${timeoutMs}ms`, 'TIMEOUT', 504)
    this.name = 'IndexerTimeoutError'
  }
}

export class IndexerUnavailableError extends IndexerError {
  constructor(
    public readonly endpoint: string,
    public readonly originalError?: Error,
  ) {
    super(`Indexer unavailable: ${endpoint}`, 'INDEXER_UNAVAILABLE', 503)
    this.name = 'IndexerUnavailableError'
  }
}
